import { useState } from 'react'
import { Button } from '@highlight/ui'
import AuthIpcRenderer from '@renderer/ipc/AuthIpc.renderer'
import {
  useAcceptLegalVersions,
  useLegalObligations,
  withCurrentVersion,
  type LegalObligationWithVersion,
} from '@renderer/hooks/use-legal-obligations'
import { legalDocumentUrl } from '@highlight/common'
import { Modal } from '../index'

/** The user-facing summary is free text from Legal; bullets and dashes become list items, blank lines are dropped. */
function summaryLines(summary: string): Array<string> {
  return summary
    .split('\n')
    .map((line) => line.replace(/^\s*(?:[•-]|\*(?!\*))\s*/, '').trim())
    .filter(Boolean)
}

/** `**text**` in Legal's summary renders bold; an unpaired marker stays literal. */
function SummaryLine({ line }: { line: string }) {
  return line.split(/\*\*(.+?)\*\*/).map((part, index) =>
    index % 2 === 1 ? (
      <strong key={index} className="font-semibold">
        {part}
      </strong>
    ) : (
      part
    ),
  )
}

function titleFor(pending: Array<LegalObligationWithVersion>): string {
  if (pending.length === 1 && pending[0].type === 'privacy_policy') return 'We’ve updated our privacy policy'
  return 'We’ve updated our terms'
}

function joinTitles<T>(items: Array<T>): Array<T | string> {
  return items.flatMap((item, index) => (index === 0 ? [item] : [' and ', item]))
}

/** Non-dismissible; declining signs out. Portaled into `container` so it blocks only the sidepanel. */
export const LegalAcceptanceModal = ({ container }: { container: HTMLElement }) => {
  const obligations = useLegalObligations(true)
  const accept = useAcceptLegalVersions()
  const [error, setError] = useState<string | null>(null)

  const pending = withCurrentVersion(obligations.data?.documents).filter((document) => document.pending)

  const handleAccept = () => {
    setError(null)
    accept.mutate(
      { versionIds: pending.map((document) => document.current.id), source: 'in_app_prompt' },
      { onError: () => setError('Couldn’t save your acceptance. Check your connection and try again.') },
    )
  }

  const handleSignOut = () => {
    AuthIpcRenderer.signOut().catch((e) => console.error('Failed to sign out from the legal prompt:', e))
  }

  const lines = pending.flatMap((document) => summaryLines(document.pendingSummary ?? ''))

  return (
    <Modal open confirm disableEscape onOpenChange={() => undefined}>
      <Modal.Content
        container={container}
        showClose={false}
        className="pointer-events-auto absolute max-h-[calc(100%-24px)] w-[calc(100%-24px)] overflow-hidden"
        backdropClassName="pointer-events-auto absolute"
      >
        <div className="flex min-h-0 flex-col gap-5 overflow-y-auto p-6">
          <div className="flex flex-col gap-1">
            <Modal.Title className="text-foreground type-h3">{titleFor(pending)}</Modal.Title>
            <Modal.Description className="type-prose-md text-foreground-secondary">
              Here’s what changed in our {joinTitles(pending.map((document) => document.title)).join('')}.
            </Modal.Description>
          </div>

          {lines.length > 0 && (
            <ul className="type-prose-md text-foreground marker:text-foreground-placeholder flex list-outside list-disc flex-col gap-2.5 pl-2.5">
              {lines.map((line) => (
                <li key={line}>
                  <SummaryLine line={line} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-foreground/[0.03] flex shrink-0 flex-col gap-4 border-t p-6">
          <p className="type-prose-md text-foreground-secondary">
            By continuing, you agree to the{' '}
            {joinTitles(
              pending.map((document) => (
                <a
                  key={document.type}
                  href={legalDocumentUrl(document.type)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground hover:text-foreground-secondary underline underline-offset-2"
                >
                  {document.title}
                </a>
              )),
            )}
            .
          </p>

          {error && <p className="type-prose-sm text-danger-subtle">{error}</p>}

          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={handleSignOut} disabled={accept.isPending}>
              Sign out
            </Button>
            <Button variant="primary" onClick={handleAccept} disabled={accept.isPending || pending.length === 0}>
              {accept.isPending ? 'Saving…' : 'Agree and continue'}
            </Button>
          </div>
        </div>
      </Modal.Content>
    </Modal>
  )
}
